import { observer } from 'mobx-react';
import React from 'react';
import 'reactflow/dist/style.css';
import { IContentDisplayItem } from '../state/CorporateArchiveStates/LegalEntityDocumentsState';
import { Icon, INavLink } from '@fluentui/react';
import folderIcon from '../img/folder.svg';
import { makeAutoObservable } from 'mobx';
import API, { IEntityTypeUserInfo } from '../API';
import { IUserConfigPackage } from '../API';
import {
	IOutlookAppCommands,
	OutlookMessageMode,
} from '../state/CorporateArchiveStates/LegalEntityDocumentsStateOutlook';
import LocationLineItemCtrl, {
	LocationLineItemCtrlState,
} from './LocationLineItemCtrl';
import { EmlReader } from '../emailParser/EmlReader';

interface ILegalEntityDocumentProps {
	state: LegalEntityDocumentState;
}

class LegalEntityDocument extends React.Component<
	ILegalEntityDocumentProps,
	{}
> {
	render() {
		return <LocationLineItemCtrl state={this.props.state.lineItemDisplay} />;
	}

	public static getIcon(d: IContentDisplayItem): React.ReactNode {
		if (d.type === 'folder')
			return (
				<img
					alt={'folder icon'}
					src={folderIcon}
					style={{ width: 22, marginLeft: -2 }}
				/>
			);
		else {
			if (d.icon) {
				if (d.icon.endsWith('pdf.png')) {
					//The png doesn't work so use our own.
					return <Icon iconName="PDF" style={{ fontSize: 18, color: '#b52222' }} />;
				}

				if (d.icon.endsWith('zip.gif')) {
					//The png doesn't work so use our own.
					return (
						<Icon iconName="ZipFolder" style={{ fontSize: 18, color: '#a9a205' }} />
					);
				}

				if (d.icon.endsWith('msg.png')) {
					//The png doesn't work so use our own.
					return <Icon iconName="Mail" style={{ fontSize: 18, color: '#222cb5' }} />;
				}

				if (d.icon.endsWith('docx.png')) {
					return (
						<Icon
							iconName="WordDocument"
							style={{ fontSize: 18, color: '#185ABD' }}
						/>
					);
				}

				if (d.icon.endsWith('xlsx.png')) {
					return (
						<Icon
							iconName="ExcelDocument"
							style={{ fontSize: 18, color: '#107C41' }}
						/>
					);
				}

				if (d.icon.indexOf('.') === -1) {
					//It's a fluent UI icon name
					return (
						<Icon iconName={d.icon} style={{ fontSize: 18, color: '#aaaaaa' }} />
					);
				}
			}

			if ((d.name || '').indexOf('.') > 0) {
				return this.getDocumentIcon(d.name);
			} else {
				return this.getDocumentIcon(d.webUrl);
			}
		}
	}

	public static getDocumentIcon(fileName: string) {
		if (fileName) {
			if (
				fileName.endsWith('jpeg') ||
				fileName.endsWith('jpg') ||
				fileName.endsWith('png') ||
				fileName.endsWith('gif')
			) {
				return (
					<Icon iconName="PictureFill" style={{ fontSize: 18, color: '#717171' }} />
				);
			}

			if (fileName.endsWith('.pdf')) {
				return <Icon iconName="PDF" style={{ fontSize: 18, color: '#b52222' }} />;
			}

			if (fileName.endsWith('.zip')) {
				return (
					<Icon iconName="ZipFolder" style={{ fontSize: 18, color: '#a9a205' }} />
				);
			}

			if (fileName.endsWith('.msg') || fileName.endsWith('.eml')) {
				return <Icon iconName="Mail" style={{ fontSize: 18, color: '#222cb5' }} />;
			}

			if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
				return (
					<Icon iconName="WordDocument" style={{ fontSize: 18, color: '#185ABD' }} />
				);
			}

			if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
				return (
					<Icon
						iconName="ExcelDocument"
						style={{ fontSize: 18, color: '#107C41' }}
					/>
				);
			}
		}

		//There's no icon, determine one from the filename
		return <Icon iconName="Page" style={{ fontSize: 18 }} />;
	}
}

export default observer(LegalEntityDocument);

export class LegalEntityDocumentState {
	public document: IContentDisplayItem;
	public commands: IOutlookAppCommands;

	private userConfig: IUserConfigPackage;
	private entityType?: IEntityTypeUserInfo;
	public lineItemDisplay: LocationLineItemCtrlState;

	constructor(
		doc: IContentDisplayItem,
		userConfig: IUserConfigPackage,
		commands: IOutlookAppCommands,
		entityType?: IEntityTypeUserInfo,
	) {
		makeAutoObservable(this);
		this.document = doc;
		this.commands = commands;
		this.userConfig = userConfig;
		this.entityType = entityType;
		this.lineItemDisplay = new LocationLineItemCtrlState(
			commands,
			this.buildNavLinks.bind(this),
			doc.name
		);
		this.lineItemDisplay.setCanExpand(false);
		this.lineItemDisplay.setIconInfo(doc);
	}

	public async launchWindow() {
		//Create the preview document url
		let url = `${window.location.origin}/#documentViewerTab/${encodeURIComponent(this.document.webUrl)}`;
		await this.commands.launchWindow(url);
	}

	public insertHTMLLink(d: IContentDisplayItem) {
		let link = `<a href='${d.webUrl}'>${d.name || 'Document'}</a>`;
		this.commands?.insertHTMLLink(link);
	}

	public async insertAttachmentFile(d: IContentDisplayItem) {
		var api = new API(await this.userConfig.getToken());
		let downloadLink = await api.prepareDownloadLink(d.webUrl);
		this.commands?.insertFileAttachment(d.name || 'document', downloadLink);
	}

	public async insertAttachmentFileAsPDF(d: IContentDisplayItem) {
		var api = new API(await this.userConfig.getToken());
		let pdf = await api.convertToPDF(d.webUrl);
		let downloadLink = await api.prepareDownloadLink(pdf);

		//Change the file extension from anything to .pdf
		let pdfDocumentName = (d.name || 'document').toLowerCase().replace(/\.docx?$/i, '.pdf');

		this.commands?.insertFileAttachment(pdfDocumentName, downloadLink);
	}

	public async useAsEmailTemplate(d: IContentDisplayItem) {
		var api = new API(await this.userConfig.getToken());
		let emlFile = await api.prepareDownloadLink(d.webUrl);
		let emlContent = await (await fetch(emlFile)).arrayBuffer();
		const reader = new EmlReader(emlContent);
		this.commands?.useAsEmailTemplate((reader.getTo() || '').split(';'), reader.getSubject(), reader.getMessageHtml());
	}

	public async replyInline(d: IContentDisplayItem) {
		try {
			const api = new API(await this.userConfig.getToken());
			const emlFile = await api.prepareDownloadLink(d.webUrl);
			const emlContent = await (await fetch(emlFile)).arrayBuffer();
			const reader = new EmlReader(emlContent);
			const from = reader.getReplyTo() || reader.getFrom() || '';
			const to = reader.getTo() || '';
			const sent = reader.getDate();
			await this.commands?.replyInline(to, from, sent, reader.getSubject(), reader.getMessageHtml());
		} catch (error) {
			console.error('Failed to reply inline:', error);
		}
	}

	private buildNavLinks(): INavLink[] {
		let navLinks: INavLink[] = [];

		if (this.document.name.toLowerCase().endsWith(".eml")) {
			/*
			navLinks.push({
				key: 'useAsTemplateItem',
				name: 'Use as email template',
				icon: 'Email',
				url: '',
				onClick: () => {
					this.lineItemDisplay.ctxMenu.toggleCommands(false);
					this.useAsEmailTemplate(this.document);
				},
			});
			*/

			navLinks.push({
				key: 'replyInlineItem',
				name: this.commands.getMessageMode() === OutlookMessageMode.Compose ? 'Reply inline' : 'Reply',
				icon: 'Reply',
				url: '',
				onClick: () => {
					this.lineItemDisplay.ctxMenu.toggleCommands(false);
					this.replyInline(this.document);
				},
			});
		}

		navLinks.push({
			key: 'viewDocumentItem',
			name: 'Preview',
			icon: 'ReadingMode',
			url: '',
			onClick: () => {
				this.lineItemDisplay.ctxMenu.toggleCommands(false);
				this.launchWindow();
			},
		});

		//Options only relevant in compose mode
		if (this.commands?.getMessageMode() === OutlookMessageMode.Compose) {
			navLinks.push({
				key: 'insertLinkItem',
				name: 'Insert Link',
				icon: 'Link',
				url: '',
				onClick: () => {
					this.lineItemDisplay.ctxMenu.toggleCommands(false);
					this.insertHTMLLink(this.document);
				},
			});

			// Only show attach file options if not prevented by entity type setting
			if (!this.userConfig.outlookPreventAttachments) {
				navLinks.push({
					key: 'attachFileItem',
					name: 'Attach File',
					icon: 'Attach',
					url: '',
					onClick: () => {
						this.lineItemDisplay.ctxMenu.toggleCommands(false);
						this.insertAttachmentFile(this.document);
					},
				});
/*
				if (this.document.name.toLowerCase().endsWith(".docx")) {
					navLinks.push({
						key: 'attachFileItemAsPDF',
						name: 'Attach File as PDF',
						icon: 'Attach',
						url: '',
						onClick: async () => {
							this.lineItemDisplay.ctxMenu.toggleCommands(false);
							await this.insertAttachmentFileAsPDF(this.document);
						},
					});
				}
					*/
			}

			if (this.document.webUrl) {
				navLinks.push({
					key: 'openLocationItem',
					name: 'Open',
					icon: 'NavigateExternalInline',
					url: '',
					onClick: (e) => {
						let path = this.document.webUrl.split('/').slice(0, -1).join('/');
						if (path.indexOf("DispForm.aspx")) {
							path = this.document.webUrl;
						}

						window.open(path, '_blank');
						this.lineItemDisplay.ctxMenu.toggleCommands(false);
					},
				});
			}
		}

		return navLinks;
	}
}
